/**
 * Juror scoring engine (DOM-free).
 *
 * Faithful port of the monolith's scoreJuror/getScoreLabel/getFlags/jurorAge.
 * The global `state.side`/`state.activeCharges` reads are replaced by an
 * explicit {@link ScoringContext} so the logic is pure and testable.
 */

import type {
  Juror,
  JurorScore,
  JurorFlag,
  ChargeSlug,
  Side,
  AgeBucket
} from '@/types';
import { CHARGES } from '@/data/charges';
import { AGE_MID } from '@/data/constants';

/** The side/charges context a juror is scored against. */
export interface ScoringContext {
  side: Side;
  activeCharges: ChargeSlug[];
}

/** With no charges selected, scoring falls back to murder (matches monolith). */
const DEFAULT_CHARGES: readonly ChargeSlug[] = ['murder'];

function chargesFor(ctx: ScoringContext): readonly ChargeSlug[] {
  return ctx.activeCharges.length ? ctx.activeCharges : DEFAULT_CHARGES;
}

/**
 * Resolve a juror's age string to a number.
 *
 * Faithful to the monolith: `parseInt` wins for numeric-leading strings, so a
 * bucket like "25-34" resolves to 25 (its lower bound) and AGE_MID is only
 * consulted for non-numeric strings. Empty/unknown resolves to 40.
 */
export function jurorAge(j: Juror): number {
  const a = j.age;
  if (!a) return 40;
  const n = parseInt(a, 10);
  if (!isNaN(n) && n >= 18 && n < 100) return n; // exact age stored as string
  return AGE_MID[a as AgeBucket] ?? 40; // age-range fallback
}

/** Score a juror in [-10, 10] from the context's side. */
export function scoreJuror(j: Juror, ctx: ScoringContext): number {
  let total = 0;
  const charges = chargesFor(ctx);
  const isDefense = ctx.side === 'defense';

  for (const ch of charges) {
    const p = CHARGES[ch];
    const w = isDefense ? p.defWeights : p.prosWeights;
    const ageN = jurorAge(j);
    const edu = j.edu || '';
    const le = j.le || 'None';
    const occ = (j.occ || '').toLowerCase();
    const biz = (j.bizType || '').toLowerCase();

    if (ageN < 35) total += w.youngAge || 0;
    if (ageN > 54) total += w.youngAge ? -w.youngAge : 0;
    // A juror's own sex is never a scoring factor (protected-class Lane 2 —
    // Soares broader than Batson); the former femaleGender/youngMale weights
    // were removed. Attitudes about a party's sex are handled via voir dire.
    if (/master|phd|doctoral|jd|law|md|medical/i.test(edu)) total += w.highEdu || 0;
    if (le !== 'None' && le !== '') total += w.lawEnforcement || 0;
    if (j.victim && j.victim !== 'No' && j.victim !== '') total += w.crimeVictim || 0;
    if (/finance|bank|insur/.test(biz) || /account|financ|invest|cpa|mba/.test(occ)) {
      total += w.businessOcc || 0;
    }
    if (/therapist|counselor|social work|psycholog/.test(occ)) total += w.therapist || 0;
    if (
      /trades|construct|labor/.test(biz) ||
      /mechanic|plumb|electr|firefight|construct|contractor/.test(occ)
    ) {
      total += w.blueCollarOcc || 0;
    }
    if (j.jury && /criminal/i.test(j.jury)) total += isDefense ? -0.5 : 0.5;
    if (/self/i.test(le)) total += w.lawEnforcement ? w.lawEnforcement * 0.8 : 0;

    // Impartiality signals
    if (j.opinion && /prosecution leaning/.test(j.opinion)) total += isDefense ? -2 : 2;
    if (j.opinion && /defense leaning/.test(j.opinion)) total += isDefense ? 2 : -2;
    if (j.opinion && /bias.*cause/.test(j.opinion)) total += isDefense ? -3 : 3;
    if (j.fair && /cannot be fair/.test(j.fair)) total += isDefense ? -4 : 4;
  }

  const max = charges.length * 9;
  return Math.max(-10, Math.min(10, Math.round((total / max) * 10)));
}

/** Map a score to its display class and label from a side's perspective. */
export function getScoreLabel(sc: number, side: Side): JurorScore {
  if (side === 'defense') {
    if (sc >= 6) return { value: sc, cls: 'score-strong-def', label: '+' + sc };
    if (sc >= 2) return { value: sc, cls: 'score-def', label: '+' + sc };
    if (sc >= -1) return { value: sc, cls: 'score-neutral', label: sc === 0 ? '0' : String(sc) };
    if (sc >= -5) return { value: sc, cls: 'score-pros', label: String(sc) };
    return { value: sc, cls: 'score-strong-pros', label: String(sc) };
  }
  if (sc <= -6) return { value: sc, cls: 'score-strong-pros', label: String(sc) };
  if (sc <= -2) return { value: sc, cls: 'score-pros', label: String(sc) };
  if (sc <= 1) return { value: sc, cls: 'score-neutral', label: sc === 0 ? '0' : String(sc) };
  if (sc <= 5) return { value: sc, cls: 'score-def', label: '+' + sc };
  return { value: sc, cls: 'score-strong-def', label: '+' + sc };
}

/** Up to 5 defense/prosecution flags surfaced for a juror. */
export function getFlags(j: Juror, ctx: ScoringContext): JurorFlag[] {
  const flags: JurorFlag[] = [];
  const charges = chargesFor(ctx);
  const ageN = jurorAge(j);
  const occ = (j.occ || '').toLowerCase();
  const biz = (j.bizType || '').toLowerCase();
  const edu = j.edu || '';
  const le = j.le || 'None';
  const side = ctx.side;
  const seen: Record<string, boolean> = {};

  const addFlag = (label: string, s: JurorFlag['side']): void => {
    if (!seen[label]) {
      seen[label] = true;
      flags.push({ label, side: s });
    }
  };

  for (const ch of charges) {
    const p = CHARGES[ch];
    const fl = side === 'defense' ? p.defFlags : p.prosFlags;
    if (ageN < 35 && fl.some((f) => /young|under 35|18-35/i.test(f))) addFlag('Young juror', side);
    if (/master|phd|doctoral|jd/i.test(edu) && fl.some((f) => /grad|educat/i.test(f))) {
      addFlag('Grad education', side);
    }
    if (le !== 'None' && le !== '') addFlag('LE connection', 'pros');
    if (j.victim && j.victim !== 'No' && j.victim !== '') addFlag('Crime victim', 'pros');
    if (j.accused && j.accused !== 'No' && j.accused !== '') addFlag('Accused family', 'def');
    if (
      (/social work|therapist|counselor/.test(occ) || /social services/.test(biz)) &&
      fl.some((f) => /social|therap|helping/i.test(f))
    ) {
      addFlag('Helping profession', side);
    }
    if (
      (/finance|bank/.test(biz) || /account|financ/.test(occ)) &&
      fl.some((f) => /financ|business/i.test(f))
    ) {
      addFlag('Finance background', 'pros');
    }
    if (j.opinion && /prosecution leaning/.test(j.opinion)) addFlag('Pros-leaning stated', 'pros');
    if (j.opinion && /defense leaning/.test(j.opinion)) addFlag('Def-leaning stated', 'def');
    if (j.opinion && /bias.*cause/.test(j.opinion)) addFlag('Cause challenge', 'pros');
    if (j.lawEmp && j.lawEmp !== 'None' && j.lawEmp !== '') addFlag('Law-related work', 'pros');
  }

  return flags.slice(0, 5);
}
